import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { getDashboardKycPipeline } from "../api/endpoints/dashboard"
import { getKycReviewDetail, getReviewerProductivity, listKycQueue } from "../api/endpoints/kyc"
import {
  Drawer,
  EmptyState,
  PageSection,
  StatCard,
  StatusPill,
  DataTable,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
} from "../components/ui"
import { formatDateTime } from "../utils/formatting"

type QueueStatusFilter = "OPEN" | "PENDING" | "ACTION_REQUIRED"

function queueEmptyState(statusFilter: QueueStatusFilter) {
  if (statusFilter === "ACTION_REQUIRED") {
    return {
      title: "No active follow-up requests",
      description: "No KYC cases are currently waiting on borrower updates.",
    }
  }

  if (statusFilter === "OPEN") {
    return {
      title: "No open verification cases",
      description: "No pending reviews or borrower follow-up cases match the current search.",
    }
  }

  return {
    title: "No pending reviews",
    description: "The active search returned no pending KYC cases.",
  }
}

export function VerificationMonitoringPage() {
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<QueueStatusFilter>("OPEN")
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)

  const queueQuery = useQuery({
    queryKey: ["kyc-monitor-queue", search, statusFilter],
    queryFn: () =>
      listKycQueue({
        ...(search ? { search } : {}),
        status: statusFilter,
        per_page: "25",
      }),
  })
  const pipelineQuery = useQuery({
    queryKey: ["kyc-monitor-pipeline"],
    queryFn: () => getDashboardKycPipeline(),
  })
  const reviewersQuery = useQuery({
    queryKey: ["kyc-monitor-reviewers"],
    queryFn: () => getReviewerProductivity(),
  })
  const detailQuery = useQuery({
    queryKey: ["kyc-monitor-detail", selectedClientId],
    queryFn: () => getKycReviewDetail(selectedClientId!),
    enabled: Boolean(selectedClientId),
  })

  const queueRows = queueQuery.data?.data ?? []
  const pipeline = pipelineQuery.data?.data
  const reviewers = reviewersQuery.data?.data?.reviewers ?? []
  const detail = detailQuery.data?.data
  const pendingRow = (pipeline?.summary ?? []).find((row: any) => row.kycStatus === "PENDING")
  const actionRequiredRow = (pipeline?.summary ?? []).find((row: any) => row.kycStatus === "ACTION_REQUIRED")
  const approvedRow = (pipeline?.summary ?? []).find((row: any) => row.kycStatus === "APPROVED")
  const rejectedRow = (pipeline?.summary ?? []).find((row: any) => row.kycStatus === "REJECTED")
  const emptyState = queueEmptyState(statusFilter)

  return (
    <div className="space-y-6">
      <PageSection
        title="Verification / KYC"
        actions={
          <div className="flex items-center gap-3">
            <select
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as QueueStatusFilter)}
            >
              <option value="OPEN">All open cases</option>
              <option value="PENDING">Pending review</option>
              <option value="ACTION_REQUIRED">Waiting on client</option>
            </select>
            <input
              className="w-64 px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-slate-50 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              placeholder="Search by name, phone, or ID"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        }
      >
        <p className="text-sm text-slate-500">
          Identity verification pipeline monitoring view. Open cases include pending reviews and
          borrowers who still need to respond to a follow-up request.
        </p>
      </PageSection>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard label="Pending Reviews" value={pendingRow?.clientCount ?? queueRows.length} />
        <StatCard label="Action Required" value={actionRequiredRow?.clientCount ?? 0} />
        <StatCard label="Approved" value={approvedRow?.clientCount ?? 0} />
        <StatCard label="Rejected" value={rejectedRow?.clientCount ?? 0} />
        <StatCard label="Recent Reviews" value={pipeline?.recentReviews?.length ?? 0} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PageSection title="Verification Queue">
          {queueRows.length === 0 ? (
            <EmptyState title={emptyState.title} description={emptyState.description} />
          ) : (
            <div className="overflow-x-auto">
              <DataTable>
                <TableHead>
                  <Th>Client</Th>
                  <Th>Phone</Th>
                  <Th>Application Date</Th>
                  <Th>Queue Age</Th>
                  <Th>Status</Th>
                </TableHead>
                <TableBody>
                  {queueRows.map((item: any) => (
                    <TableRow key={item.clientId} onClick={() => setSelectedClientId(item.clientId)}>
                      <Td>
                        <strong>{item.fullName}</strong>
                        <div className="text-xs text-slate-500">{item.clientId}</div>
                      </Td>
                      <Td>{item.phoneNumber}</Td>
                      <Td>{formatDateTime(item.createdAt)}</Td>
                      <Td>{item.queueAgeDays}d</Td>
                      <Td>
                        <StatusPill tone={item.kycStatus === "ACTION_REQUIRED" ? "info" : "warning"}>
                          {item.kycStatus}
                        </StatusPill>
                      </Td>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </div>
          )}
        </PageSection>

        <PageSection title="Reviewer Performance">
          {reviewers.length === 0 ? (
            <EmptyState
              title="No reviewer activity"
              description="No reviewer metrics are available in the current environment."
            />
          ) : (
            <div className="overflow-x-auto">
              <DataTable>
                <TableHead>
                  <Th>Reviewer</Th>
                  <Th>Reviews</Th>
                  <Th>Approved</Th>
                  <Th>Rejected</Th>
                  <Th>Rejection Rate</Th>
                  <Th>Last Active</Th>
                </TableHead>
                <TableBody>
                  {reviewers.map((reviewer: any) => (
                    <TableRow key={reviewer.reviewerId}>
                      <Td>{reviewer.reviewerName}</Td>
                      <Td>{reviewer.totalReviews}</Td>
                      <Td>{reviewer.approvedCount}</Td>
                      <Td>{reviewer.rejectedCount}</Td>
                      <Td>{reviewer.rejectionRate}%</Td>
                      <Td>{formatDateTime(reviewer.lastActiveAt)}</Td>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </div>
          )}
        </PageSection>

        <PageSection title="Pending Aging">
          {(pipeline?.pendingAging ?? []).length === 0 ? (
            <EmptyState
              title="No pending aging data"
              description="Pending KYC cases will appear here when available."
            />
          ) : (
            <div className="overflow-x-auto">
              <DataTable>
                <TableHead>
                  <Th>Age Bucket</Th>
                  <Th>Clients</Th>
                </TableHead>
                <TableBody>
                  {(pipeline?.pendingAging ?? []).map((row: any) => (
                    <TableRow key={row.ageBucket}>
                      <Td>{row.ageBucket}</Td>
                      <Td>{row.clientCount}</Td>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </div>
          )}
        </PageSection>

        <PageSection title="Recent Reviews">
          {(pipeline?.recentReviews ?? []).length === 0 ? (
            <EmptyState
              title="No recent decisions"
              description="Completed reviews will appear here once the queue is worked."
            />
          ) : (
            <div className="overflow-x-auto">
              <DataTable>
                <TableHead>
                  <Th>Client</Th>
                  <Th>Status</Th>
                  <Th>Reviewer</Th>
                  <Th>Reviewed At</Th>
                </TableHead>
                <TableBody>
                  {(pipeline?.recentReviews ?? []).map((row: any) => (
                    <TableRow key={row.clientId}>
                      <Td>{row.fullName}</Td>
                      <Td>{row.kycStatus}</Td>
                      <Td>{row.reviewerName ?? "-"}</Td>
                      <Td>{formatDateTime(row.kycReviewedAt)}</Td>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </div>
          )}
        </PageSection>
      </div>

      <Drawer
        title={detail?.fullName ?? "KYC summary"}
        open={Boolean(selectedClientId)}
        onClose={() => setSelectedClientId(null)}
      >
        {detail ? (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-slate-500">Client</p>
              <p className="text-sm font-semibold text-slate-900">{detail.fullName}</p>
              <p className="text-sm text-slate-500">{detail.phoneNumber}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500">Status</p>
                <StatusPill
                  tone={
                    detail.kycStatus === "APPROVED"
                      ? "success"
                      : detail.kycStatus === "ACTION_REQUIRED"
                        ? "info"
                        : "warning"
                  }
                >
                  {detail.kycStatus}
                </StatusPill>
              </div>
              <div>
                <p className="text-xs text-slate-500">Reviewer</p>
                <p className="text-slate-800">{detail.reviewerName ?? "Pending"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Application Date</p>
                <p className="text-slate-800">{formatDateTime(detail.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Decision Date</p>
                <p className="text-slate-800">{formatDateTime(detail.reviewedAt)}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">Decision Notes</p>
              <p className="text-sm text-slate-700">
                {detail.rejectionReason ?? "No rejection reason recorded."}
              </p>
            </div>
            {detail.activeRequest ? (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
                  Active request
                </p>
                <p className="mt-2 text-sm text-slate-700">{detail.activeRequest.message}</p>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Loading KYC summary...</p>
        )}
      </Drawer>
    </div>
  )
}
